import { useInterval } from '@mantine/hooks'
import type { routes } from '@semios/app-platform-banyan-route-definitions'
import { networkStore } from 'App/ServiceCenter/store/networkStore'
import { translate } from 'i18n/i18n'
import { useCallback, useEffect, useState } from 'react'
import { maintenanceStore } from 'stores/maintenanceStore/maintenanceStore'
import { apiFetch } from 'utils/apiFetch'
import { captureFrontendError } from 'utils/captureFrontendError'
import { reportApiFailure } from 'utils/reportApiFailure'

export const useCheckForHubMaintenance = () => {
  const [maintenanceMessage, setMaintenanceMessage] = useState<null | string>(null)
  const storeMessage = maintenanceStore.useSelector((s) => s.maintenanceMessage)
  const connected = networkStore.useSelector((s) => s.connectionStatus.connected)

  const checkForMaintenance = useCallback(async () => {
    let response: routes.HubMaintenance.Response

    // Check #1: Is the API up?

    try {
      response = await apiFetch({ url: '/hub-maintenance', body: null })
    } catch (error) {
      captureFrontendError(error)

      setMaintenanceMessage(
        translate.phrases.banyanApp(
          'Our system is undergoing maintenance. Some features may be temporarily unavailable.',
        ),
      )

      reportApiFailure('/hub-maintenance', 'api_unreachable')

      return
    }

    // Check #2: Are we doing maintenance?

    if (response.hubMaintenance === 'true') {
      setMaintenanceMessage(
        translate.phrases.banyanApp(
          'Our system is undergoing maintenance. Some features may be temporarily unavailable.',
        ),
      )

      reportApiFailure('/hub-maintenance', 'maintenance_active')

      return
    }

    // If we made it this far, things are ok.
    // Clear both local state and the shared store (recovers from other API failures too)

    setMaintenanceMessage(null)

    maintenanceStore.setState(() => ({ maintenanceMessage: null }))
  }, [])

  const checkForMaintenanceInterval = useInterval(checkForMaintenance, 180000) // 3 minutes

  const startCheckingForMaintenance = useCallback(() => {
    checkForMaintenance()

    checkForMaintenanceInterval.start()
  }, [])

  const stopCheckingForMaintenance = useCallback(() => {
    checkForMaintenanceInterval.stop()

    setMaintenanceMessage(null)
  }, [])

  useEffect(() => {
    // Only check for maintenance if the user is connected to the internet
    if (connected) {
      startCheckingForMaintenance()
    } else {
      stopCheckingForMaintenance()
    }

    return () => {
      stopCheckingForMaintenance()
    }
  }, [connected])

  return maintenanceMessage || storeMessage
}
